import { assignVirtualIds } from './OrderPricingPipeline';
import { shouldApplyDiscount } from './OrderRuleEvaluator';
import { pickupDateRuleApplies } from '../libraries/PickupDateRule';
import { discountTypeEnum } from '../types/EnumTypes';
import { G6LineType } from './OrderLineTypesG6';
const HEAT_BOX_LINE = {
    type: G6LineType.ReturnableItem,
    name: 'Depositum for termokasser',
    ean: '5710405560867',
    price: 200,
    unitType: 'order',
    quantity: 1,
};
const DRIVE_IN_LINE = {
    type: G6LineType.Delivery,
    name: 'Drive-in delivery',
    ean: '5712872794790',
    price: 0,
    unitType: 'order',
    quantity: 1,
};
const HOME_DELIVERY_LINE = {
    type: G6LineType.Delivery,
    name: 'Hjemmelevering',
    ean: '5712876288585',
    price: 68,
    unitType: 'order',
    quantity: 1,
};
const isEmptyArrayOrNull = (testArray) => {
    if (Array.isArray(testArray) && testArray.length > 0) {
        return false;
    }
    return true;
};
const hasIntersection = (arr1, arr2) => {
    return arr1.filter((x) => arr2.includes(x)).length > 0;
};
const isNumber = (number) => {
    return typeof number === 'number' && !isNaN(number);
};
const resolveEffectiveDiscounts = (orderLines, options) => {
    const vouchers = options.vouchers || [];
    const surviveDateGate = (discount) => {
        if (!discount)
            return false;
        if (discount.dateRule === 'pickupDate') {
            return pickupDateRuleApplies(discount.orderingDatePattern, options.orderPickupDate);
        }
        return true;
    };
    const surviveVoucherGate = (discount) => {
        if (isEmptyArrayOrNull(discount.voucherPattern)) {
            return true;
        }
        return !isEmptyArrayOrNull(vouchers) && hasIntersection(discount.voucherPattern, vouchers);
    };
    const visit = (node) => {
        if (node.discount || (Array.isArray(node.applicableDiscounts) && node.applicableDiscounts.length > 0)) {
            const candidates = Array.isArray(node.applicableDiscounts) && node.applicableDiscounts.length > 0
                ? node.applicableDiscounts
                : [node.discount];
            const effective = candidates.find((d) => surviveDateGate(d) && surviveVoucherGate(d));
            if (effective) {
                node.discount = effective;
            }
            else {
                delete node.discount;
            }
            delete node.applicableDiscounts;
        }
        (node.subLines || []).forEach(visit);
    };
    orderLines.forEach(visit);
    return orderLines;
};
export const getConfigPriceModifierG6 = (node) => {
    const result = (node.subLines || [])
        .filter((line) => line.type === G6LineType.Config && line.selectedOption)
        .reduce((carry, line) => {
        if (Array.isArray(line.selectedOption)) {
            line.selectedOption.forEach((opt) => {
                if (opt.priceModifier) {
                    carry += Math.round((opt.priceModifier.value || 0) * 100);
                }
            });
        }
        else if (line.selectedOption.priceModifier) {
            carry += Math.round((line.selectedOption.priceModifier.value || 0) * 100);
        }
        return carry;
    }, 0);
    if (result !== 0) {
        return result / 100;
    }
    return 0;
};
export const computeNodeTotalG1 = (node) => {
    if (node.includedInMainPrice) {
        return 0;
    }
    let mainPrice = 0;
    if (node.price && node.quantity) {
        mainPrice = Math.round(node.price * 100) * node.quantity;
    }
    const subLinePrice = (node.subLines || []).reduce((carry, line) => carry + computeNodeTotalG1(line), 0) * 100;
    const baseLinePrice = mainPrice + subLinePrice;
    return (baseLinePrice + getConfigPriceModifierG6(node) * 100) / 100;
};
export const getTotalPriceWithoutDiscountG6 = (orderLines, includeReturnableItems = false) => {
    let total = 0;
    for (const i in orderLines) {
        if (orderLines[i].includedInMainPrice !== true && orderLines[i].type !== G6LineType.Discount) {
            if (orderLines[i].type === G6LineType.ReturnableItem) {
                if (includeReturnableItems) {
                    total += Math.round((orderLines[i].price || 0) * 100) * (orderLines[i].quantity || 0);
                }
            }
            else {
                total += Math.round((orderLines[i].price || 0) * 100) * (orderLines[i].quantity || 0);
                total += Math.round(getTotalPriceWithoutDiscountG6(orderLines[i].subLines || [], true) * 100);
            }
        }
    }
    for (const i in orderLines) {
        if (orderLines[i].includedInMainPrice !== true) {
            if (orderLines[i].type === G6LineType.Config && orderLines[i].selectedOption) {
                if (Array.isArray(orderLines[i].selectedOption)) {
                    orderLines[i].selectedOption.forEach((opt) => {
                        if (opt.priceModifier) {
                            total += Math.round((opt.priceModifier.value || 0) * 100);
                        }
                    });
                }
                else if (orderLines[i].selectedOption.priceModifier) {
                    total += Math.round((orderLines[i].selectedOption.priceModifier.value || 0) * 100);
                }
            }
        }
    }
    return total / 100;
};
const lineBasePriceTopLevel = (line) => {
    if (line.type === 'Product') {
        const quantity = line.quantity ? line.quantity : 1;
        return line.totalPrice ? line.totalPrice : line.price * quantity;
    }
    else if (line.type === 'Aggregation') {
        return (line.subLines || []).reduce((acc, cur) => {
            return acc + lineBasePriceTopLevel(cur);
        }, 0);
    }
    else {
        return 0;
    }
};
const lineBasePriceLegacy = (orderLine, productCatalog) => {
    if (orderLine.type === G6LineType.Product) {
        const quantity = orderLine.quantity ? orderLine.quantity : 1;
        return orderLine.totalPrice ? orderLine.totalPrice : orderLine.price * quantity;
    }
    else if (orderLine.type === G6LineType.Aggregation) {
        const getSimpleBasePrice = (line) => {
            const foundLine = (line.subLines || []).find((sbLine) => {
                return sbLine.type === G6LineType.Product && sbLine.rootProductId === sbLine.productId;
            });
            if (foundLine && 'quantity' in foundLine && 'price' in foundLine) {
                const quantity = foundLine.quantity ? foundLine.quantity : 1;
                return foundLine.price * quantity;
            }
            else {
                return 0;
            }
        };
        if (productCatalog) {
            const productId = orderLine.productId ? orderLine.productId : orderLine.rootProductId;
            const productFromCatalog = productCatalog.getProduct(productId, true);
            const quantity = orderLine.quantity ? orderLine.quantity : 1;
            if (productFromCatalog) {
                const discountValue = productFromCatalog.discount && productFromCatalog.discount.discountType === 'simpleDiscountFixedAmount'
                    ? productFromCatalog.discount.discountValue
                    : 0;
                void discountValue;
                if (productFromCatalog.price) {
                    return productFromCatalog.price * quantity;
                }
                const calculatedPrice = (productFromCatalog.groups || []).reduce((accumulator, currentValue) => {
                    const groupProduct = currentValue.options.defaultSelected
                        ? productCatalog.getProduct(currentValue.options.defaultSelected, true)
                        : null;
                    if (currentValue.options.showIfConfigurationOptionSelected) {
                        const config = productFromCatalog.configurations.find((c) => c.id === currentValue.options.showIfConfigurationOptionSelected.configurationId);
                        const index = config.options.defaultSelected;
                        if (index !== currentValue.options.showIfConfigurationOptionSelected.optionIndex) {
                            return accumulator;
                        }
                    }
                    return accumulator + (groupProduct ? groupProduct.getPriceInGroup() : 0);
                }, 0);
                return calculatedPrice * quantity;
            }
            return getSimpleBasePrice(orderLine);
        }
        else {
            return getSimpleBasePrice(orderLine);
        }
    }
    return 0;
};
const computeMoneyG6 = (orderLines, productCatalog, session) => {
    void session;
    const setNodeAccums = (node) => {
        node.totalPriceAccum = computeNodeTotalG1(node);
        (node.subLines || []).forEach(setNodeAccums);
    };
    orderLines.forEach(setNodeAccums);
    const isSingleSelectionProduct = (orderLine) => {
        return orderLine.options && orderLine.options.canHaveMultipleProductSelections;
    };
    const applicableDiscountForMoney = (orderLine) => {
        if (!('discount' in orderLine) || !orderLine.discount) {
            return undefined;
        }
        const discount = Object.assign(Object.assign({}, orderLine.discount), { productId: orderLine.rootProductId });
        const discountApplicableGames = (() => {
            if (!discount.options) {
                return false;
            }
            if ([undefined, null].includes(discount.options.giftDiscount)) {
                return true;
            }
            if (discount.options.giftDiscount === true && productCatalog) {
                const product = productCatalog.getProduct(discount.productId);
                if (product && product.options.isPrize && product.options.isPrize.fromGame && orderLine.addedByGame) {
                    return true;
                }
            }
            return false;
        })();
        return discountApplicableGames ? discount : undefined;
    };
    const calculateSimpleDiscountFixedAmount = (line) => {
        if (!isNaN(line.discount.discountValue) && typeof line.discount.discountValue === 'number') {
            const discountedPrice = line.totalPrice * 100 - line.quantity * line.discount.discountValue * 100;
            return discountedPrice >= 0 ? discountedPrice / 100 : line.totalPrice;
        }
        return line.totalPrice;
    };
    const singleLineProductQuantity = {};
    const moneyLines = [];
    for (const i in orderLines) {
        const orderLine = orderLines[i];
        moneyLines.push({
            productId: 'rootProductId' in orderLine ? orderLine.rootProductId : undefined,
            options: orderLine.options,
            totalPrice: getTotalPriceWithoutDiscountG6([orderLine], false),
            discount: applicableDiscountForMoney(orderLine),
            basePrice: lineBasePriceLegacy(orderLine, productCatalog),
            type: orderLine.type,
            quantity: isSingleSelectionProduct(orderLine) ? 1 : 'quantity' in orderLine ? orderLine.quantity : undefined,
        });
    }
    for (const i in moneyLines) {
        const orderLine = orderLines[i];
        if (isSingleSelectionProduct(orderLine)) {
            if (typeof singleLineProductQuantity[orderLine.rootProductId] === 'number') {
                singleLineProductQuantity[orderLine.rootProductId] += 1;
            }
            else {
                singleLineProductQuantity[orderLine.rootProductId] = 1;
            }
        }
        const line = moneyLines[i];
        const undiscounted = line.totalPrice;
        if (line.discount) {
            const discountType = line.discount.discountType;
            if (discountType === 'simpleDiscountFixedAmount') {
                line.totalPrice = calculateSimpleDiscountFixedAmount(line);
            }
            else if (discountType === 'bulkDiscount' && !isSingleSelectionProduct(line)) {
                const minQuantityToApply = parseFloat(line.discount.options.minQuantityToApply);
                const newUnitPrice = parseFloat(line.discount.options.newUnitPrice);
                if (isNumber(minQuantityToApply) && isNumber(newUnitPrice) && line.quantity >= minQuantityToApply) {
                    line.totalPrice = newUnitPrice * line.quantity;
                }
            }
            else if (discountType === 'percentDiscount') {
                if (isNumber(line.discount.discountValue)) {
                    if (line.discount.options && line.discount.options.discountBasePriceOnly) {
                        line.totalPrice =
                            (line.totalPrice * 100 - line.basePrice * Math.min(100, line.discount.discountValue)) / 100;
                    }
                    else {
                        line.totalPrice =
                            (line.totalPrice * 100 - line.totalPrice * Math.min(100, line.discount.discountValue)) / 100;
                    }
                }
            }
            else if (discountType === 'twoForOne' && !isSingleSelectionProduct(line)) {
                line.totalPrice = (line.totalPrice * 100) / 2 / 100;
            }
            else if (discountType === 'twoForOne' && isSingleSelectionProduct(line)) {
                if (2 === singleLineProductQuantity[orderLine.rootProductId]) {
                    const discountValue = isNumber(line.discount.discountValue) ? line.discount.discountValue : line.basePrice;
                    const discountedTotal = line.totalPrice * 100 - discountValue * 100;
                    line.totalPrice = isNumber(discountedTotal) && discountedTotal >= 0 ? discountedTotal / 100 : line.totalPrice;
                    singleLineProductQuantity[orderLine.rootProductId] = 0;
                }
            }
        }
        if (orderLine.type === G6LineType.Discount) {
            orderLine.totalPriceAccum = orderLine.price || 0;
            orderLine.totalPriceBeforeAccum = 0;
        }
        else if (orderLine.type === G6LineType.ReturnableItem) {
            const ownTotal = (Math.round((orderLine.price || 0) * 100) * (orderLine.quantity || 0)) / 100;
            orderLine.totalPriceAccum = ownTotal;
            orderLine.totalPriceBeforeAccum = ownTotal;
        }
        else {
            orderLine.totalPriceAccum = line.totalPrice;
            orderLine.totalPriceBeforeAccum = undiscounted;
        }
    }
    return orderLines;
};
const appendTopLevelLinesG6 = (orderLines, productCatalog, options, addHeatBox) => {
    var _a;
    if (addHeatBox) {
        orderLines.push(Object.assign(Object.assign({}, HEAT_BOX_LINE), { generation: 6, includedInMainPrice: false, subLines: [] }));
    }
    const deliveryChoice = options.deliveryChoice;
    if (deliveryChoice === 2 && typeof options.driveInDelivery === 'object' && options.driveInDelivery !== null) {
        const { name = DRIVE_IN_LINE.name, price = 0 } = options.driveInDelivery;
        orderLines.push(Object.assign(Object.assign({}, DRIVE_IN_LINE), { name,
            price, generation: 6, includedInMainPrice: false, subLines: [] }));
    }
    if (deliveryChoice === 3) {
        orderLines.push(Object.assign(Object.assign({}, HOME_DELIVERY_LINE), { generation: 6, includedInMainPrice: false, subLines: [] }));
    }
    const filteredRules = (productCatalog && productCatalog.priceRules) || [];
    const filterBasedOnVoucher = (rule) => shouldApplyDiscount(rule, { vouchers: options.vouchers });
    const globalRules = filteredRules.filter((rule) => !rule.productSpecific && rule.discountType === discountTypeEnum.globalDiscountWithMax);
    const computedGlobalPriceRules = globalRules.find(filterBasedOnVoucher);
    const pushTotalRabat = (roundedDiscountSum) => {
        orderLines.push({
            type: G6LineType.Discount,
            name: 'Total Rabat',
            quantity: 1,
            unitType: '',
            price: -1 * roundedDiscountSum,
            generation: 6,
            includedInMainPrice: false,
            subLines: [],
        });
    };
    if (computedGlobalPriceRules) {
        const perc = computedGlobalPriceRules.options.discountPct;
        const discountSum = orderLines.reduce((acc, cur) => {
            return acc + lineBasePriceTopLevel(cur) * perc;
        }, 0);
        pushTotalRabat(Math.round(discountSum * 100) / 100);
    }
    if (options.promoCode && options.employeeDiscount && options.employeeDiscount.active) {
        const perc = options.employeeDiscount.percent || 0;
        const discountSum = orderLines.reduce((acc, cur) => {
            return acc + (lineBasePriceTopLevel(cur) * perc) / 100;
        }, 0);
        pushTotalRabat(Math.round(discountSum * 100) / 100);
    }
    if ((_a = options.loyaltySystemDiscount) === null || _a === void 0 ? void 0 : _a.discountAmount) {
        pushTotalRabat(options.loyaltySystemDiscount.discountAmount);
    }
    return orderLines;
};
export const runOrderPricingPipelineG6 = ({ productCatalog, orderLines, options = {}, orderTags = [], session, addHeatBox = false, }) => {
    void orderTags;
    resolveEffectiveDiscounts(orderLines, options);
    appendTopLevelLinesG6(orderLines, productCatalog, options, addHeatBox);
    computeMoneyG6(orderLines, productCatalog, session);
    assignVirtualIds(orderLines);
    return orderLines;
};
